import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/common/button';
import { Input } from '@/components/common/input';
import { Modal } from '@/components/common/modal';
import {
  Camera,
  Calendar,
  Droplets,
  TrendingUp,
  AlertCircle,
  X,
} from 'lucide-react';
import { Meter, CreateMeterReadingPayload } from '@/types/meter_types';

interface MeterReadingFormProps {
  is_open: boolean;
  on_close: () => void;
  on_submit: (data: CreateMeterReadingPayload) => void;
  meter: Meter | null;
  is_loading?: boolean;
}

// Helper to get billing periods
const getBillingPeriods = () => {
  const periods = [];
  const today = new Date();
  
  // Generate last 12 months of periods
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0); // Last day of month
    
    periods.push({
      label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      value: `${year}-${String(month + 1).padStart(2, '0')}`,
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
      month: month + 1,
      year: year
    });
  }
  
  return periods;
};

export const MeterReadingForm: React.FC<MeterReadingFormProps> = ({
  is_open,
  on_close,
  on_submit,
  meter,
  is_loading = false
}) => {
  const [billing_period, set_billing_period] = useState('');
  const [form_data, set_form_data] = useState<{
    reading_value: string;
    reading_date: string;
    notes: string;
    photo?: File;
  }>({
    reading_value: '',
    reading_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [errors, set_errors] = useState<Record<string, string>>({});
  const [photo_preview, set_photo_preview] = useState<string | null>(null);
  const file_input_ref = useRef<HTMLInputElement>(null);

  const billing_periods = getBillingPeriods();


  // Reset form when modal opens/closes or meter changes
  useEffect(() => {
    if (is_open && meter) {
      // Default to current month
      const current_period = billing_periods[0];
      set_billing_period(current_period.value);
      
      set_form_data({
        reading_value: '',
        reading_date: current_period.end_date, // Default to end of month
        notes: ''
      });
      set_photo_preview(null);
      set_errors({});
    }
  }, [is_open, meter]);

  // Update reading date when period changes
  useEffect(() => {
    if (billing_period) {
      const period = billing_periods.find(p => p.value === billing_period);
      if (period) {
        set_form_data(prev => ({ 
          ...prev, 
          reading_date: period.end_date 
        }));
      }
    }
  }, [billing_period]);

  const validate_form = (): boolean => {
    const new_errors: Record<string, string> = {};

    if (!billing_period) {
      new_errors.billing_period = 'Please select a billing period';
    }

    if (!form_data.reading_value.trim()) {
      new_errors.reading_value = 'Reading value is required';
    } else {
      const value = parseFloat(form_data.reading_value);
      if (isNaN(value) || value < 0) {
        new_errors.reading_value = 'Reading must be a positive number';
      } else if (meter?.last_reading_value && value < meter.last_reading_value) {
        new_errors.reading_value = `Reading cannot be less than last reading (${meter.last_reading_value.toLocaleString()} m³)`;
      }
    }

    if (!form_data.reading_date) {
      new_errors.reading_date = 'Reading date is required';
    } else {
      const selected_date = new Date(form_data.reading_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (selected_date > today) {
        new_errors.reading_date = 'Reading date cannot be in the future';
      }

      if (meter?.last_reading_date) {
        const last_date = new Date(meter.last_reading_date);
        if (selected_date < last_date) {
          new_errors.reading_date = `Reading date cannot be before last reading date (${last_date.toLocaleDateString()})`;
        }
      }
    }

    set_errors(new_errors);
    return Object.keys(new_errors).length === 0;
  };

  const handle_submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!meter) return;

    if (validate_form()) {
      const payload: CreateMeterReadingPayload = {
          meter: meter.id,
          current_reading: parseFloat(form_data.reading_value),
          reading_date: form_data.reading_date,
          notes: form_data.notes || undefined,
          photo: form_data.photo,
          meter_id: '',
          reading_value: 0
      };

      on_submit(payload);
    }
  };

  const handle_change = (field: keyof typeof form_data, value: any) => {
    set_form_data(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      set_errors(prev => {
        const new_errors = { ...prev };
        delete new_errors[field];
        return new_errors;
      });
    }
  };

  const handle_photo_change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        set_errors(prev => ({ ...prev, photo: 'Please select an image file' }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        set_errors(prev => ({ ...prev, photo: 'Image size must be less than 5MB' }));
        return;
      }

      set_form_data(prev => ({ ...prev, photo: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        set_photo_preview(reader.result as string);
      };
      reader.readAsDataURL(file);

      if (errors.photo) {
        set_errors(prev => {
          const new_errors = { ...prev };
          delete new_errors.photo;
          return new_errors;
        });
      }
    }
  };

  const handle_remove_photo = () => {
    set_form_data(prev => ({ ...prev, photo: undefined }));
    set_photo_preview(null);
    if (file_input_ref.current) {
      file_input_ref.current.value = '';
    }
  };

  const calculate_consumption = () => {
    if (!meter?.last_reading_value || !form_data.reading_value) return null;
    const current = parseFloat(form_data.reading_value);
    if (isNaN(current)) return null;
    return current - meter.last_reading_value;
  };

  const consumption = calculate_consumption();
  const handle_close = () => {
    if (!is_loading) {
      on_close();
    }
  };

  if (!meter) return null;

  const selected_period = billing_periods.find(p => p.value === billing_period);

  return (
    <Modal
      is_open={is_open}
      on_close={handle_close}
      title="Record Meter Reading"
      size="lg"
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={handle_close}
            disabled={is_loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handle_submit}
            is_loading={is_loading}
          >
            Save Reading
          </Button>
        </div>
      }
    >
      <form onSubmit={handle_submit} className="space-y-6">
        {/* Meter Information */}
        <div className="p-5 bg-gradient-to-r from-forest-green to-dark-forest rounded-md text-white border border-light-gray">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-md bg-lime-green text-forest-green flex items-center justify-center flex-shrink-0">
              <Droplets size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold">{meter.meter_number}</h3>
              <p className="text-sm opacity-90">{meter.client_name}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white border-opacity-20">
            <div>
              <p className="text-xs opacity-75 mb-1">Location</p>
              <p className="text-sm font-semibold">{meter.notes}</p>
            </div>
            {meter.initial_reading && (
              <div>
                <p className="text-xs opacity-75 mb-1">Last Reading</p>
                <p className="text-sm font-semibold">
                  {meter.initial_reading.toLocaleString()} Units
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Billing Period Selection */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Billing Period
          </h3>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-1.5">
              Select Period <span className="text-error">*</span>
            </label>
            <select
              value={billing_period}
              onChange={(e) => {
                set_billing_period(e.target.value);
                if (errors.billing_period) {
                  set_errors(prev => {
                    const new_errors = { ...prev };
                    delete new_errors.billing_period;
                    return new_errors;
                  });
                }
              }}
              className="w-full px-4 py-2.5 border border-light-gray rounded-md focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all bg-white text-charcoal"
            >
              <option value="">Select billing period...</option>
              {billing_periods.map(period => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
            {errors.billing_period && (
              <p className="mt-1.5 text-sm text-error flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.billing_period}
              </p>
            )}
            
            {selected_period && (
              <p className="mt-1.5 text-xs text-medium-gray">
                Period: {selected_period.start_date} to {selected_period.end_date}
              </p>
            )}
          </div>

          {/* Reading Date (auto-filled, but editable) */}
          <Input
            label="Reading Date"
            type="date"
            value={form_data.reading_date}
            onChange={(e) => handle_change('reading_date', e.target.value)}
            icon_left={<Calendar size={18} />}
            error={errors.reading_date}
            is_required
            helper_text="Defaults to end of selected period, but you can adjust if needed"
          />
        </div>

        {/* Reading Value */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Meter Reading
          </h3>

          <Input
            label="Current Reading (m³)"
            type="number"
            step="0.01"
            placeholder="Enter current meter reading"
            value={form_data.reading_value}
            onChange={(e) => handle_change('reading_value', e.target.value)}
            icon_left={<Droplets size={18} />}
            error={errors.reading_value}
            is_required
            helper_text="Enter the current value shown on the meter"
          />

          {/* Consumption Preview */}
          {consumption !== null && consumption >= 0 && (
            <div className="p-4 bg-lime-green bg-opacity-10 border border-lime-green rounded-md">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-lime-green text-forest-green flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={20} />
                </div>
                <div>
                  <p className="text-sm text-medium-gray mb-1">Calculated Consumption</p>
                  <p className="text-2xl font-bold text-forest-green">
                    {consumption.toLocaleString()} m³
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Photo Upload */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Photo Evidence (Optional)
          </h3>

          {photo_preview ? (
            <div className="relative">
              <img
                src={photo_preview}
                alt="Meter reading"
                className="w-full h-64 object-cover rounded-md border-2 border-light-gray"
              />
              <button
                type="button"
                onClick={handle_remove_photo}
                className="absolute top-2 right-2 p-2 bg-error text-white rounded-md hover:bg-opacity-90 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div
              onClick={() => file_input_ref.current?.click()}
              className="border-2 border-dashed border-light-gray rounded-md p-8 text-center hover:border-forest-green hover:bg-forest-green hover:bg-opacity-5 transition-all cursor-pointer"
            >
              <Camera size={48} className="mx-auto text-medium-gray mb-3" />
              <p className="text-sm font-medium text-charcoal mb-1">
                Click to upload meter photo
              </p>
              <p className="text-xs text-medium-gray">
                PNG, JPG up to 5MB
              </p>
            </div>
          )}

          <input
            ref={file_input_ref}
            type="file"
            accept="image/*"
            onChange={handle_photo_change}
            className="hidden"
          />

          {errors.photo && (
            <p className="text-sm text-error flex items-center gap-1">
              <AlertCircle size={14} />
              {errors.photo}
            </p>
          )}
        </div>

        {/* Notes */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Additional Notes
          </h3>

          <div>
            <textarea
              value={form_data.notes}
              onChange={(e) => handle_change('notes', e.target.value)}
              placeholder="Any observations or notes about this reading..."
              rows={4}
              className="w-full px-4 py-3 border border-light-gray rounded-md focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all resize-none bg-white text-charcoal"
            />
            <p className="mt-1.5 text-xs text-medium-gray">
              Optional: Add any relevant observations or issues noticed during reading
            </p>
          </div>
        </div>

      </form>
    </Modal>
  );
};